import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { API, formatApiErrorDetail } from "../../lib/api";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine,
} from "recharts";
import {
  Sparkles, FilePlus, ShieldCheck, AlertTriangle, FileText, CreditCard,
  Loader2, MessageCircle, Zap, Download, Radio, Scan,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import KycCard from "../../components/KycCard";
import MerchantDocTimeline from "../../components/MerchantDocTimeline";
import ReferralCard from "../../components/ReferralCard";
import CoachWidget from "../../components/CoachWidget";
import ClaimTimeline from "../../components/ClaimTimeline";
import { toast, Toaster } from "sonner";

const TIER_LABEL = {
  merchant_lite: "Merchant Lite",
  social_vendor_pro: "Social Vendor Pro",
  enterprise_sme: "Enterprise SME",
};

const CLAIM_BADGE = {
  paid: "badge-paid", flash_approved: "badge-flash",
  manual_review: "badge-review", validating: "badge-validating",
  rejected: "badge-rejected", escalated: "badge-suspended",
  siu_review: "badge-suspended", submitted: "badge-pending", closed: "badge-cooling",
};

export default function MerchantDashboard() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [paying, setPaying] = useState(false);
  const [live, setLive] = useState({ connected: false, beats: 0 });
  const sseRef = useRef(null);

  function load() { api.get("/merchant/me").then((r) => setData(r.data)); }
  useEffect(load, []);

  // Real-time SSE: replace the previous polling.
  useEffect(() => {
    const token = sessionStorage.getItem("nyk_token");
    const url = `${API}/merchant/stream${token ? `?token=${encodeURIComponent(token)}` : ""}`;
    let es;
    try { es = new EventSource(url, { withCredentials: true }); }
    catch (_) { return undefined; }
    sseRef.current = es;
    es.addEventListener("open", () => setLive((s) => ({ ...s, connected: true })));
    es.addEventListener("heartbeat", (ev) => {
      try {
        const h = JSON.parse(ev.data);
        setLive((s) => ({ connected: true, beats: s.beats + 1 }));
        setData((cur) => cur ? {
          ...cur,
          merchant: {
            ...cur.merchant,
            risk_score: h.risk_score ?? cur.merchant.risk_score,
            kyc_status: h.kyc_status ?? cur.merchant.kyc_status,
            free_weeks_credit: h.free_weeks_credit ?? cur.merchant.free_weeks_credit,
            status: h.merchant_status ?? cur.merchant.status,
          },
        } : cur);
      } catch (_) { /* ignore */ }
    });
    es.addEventListener("notification", (ev) => {
      try {
        const n = JSON.parse(ev.data);
        if (!n.read_at) {
          toast.message(n.title || "Notification", { description: n.body || "" });
          // Refresh dashboard payload to surface new claims/payouts/etc.
          load();
        }
      } catch (_) { /* ignore */ }
    });
    es.onerror = () => setLive((s) => ({ ...s, connected: false }));
    return () => { es && es.close(); sseRef.current = null; };
  }, []);

  async function payPremium() {
    setPaying(true);
    try {
      const { data: r } = await api.post("/merchant/pay-premium");
      if (r.rebate_applied) {
        toast.success(`Paid KES ${r.amount} (saved KES ${r.rebate_amount} with loyalty rebate)`);
      } else {
        toast.success(`Paid KES ${r.amount} — ref ${r.reference}`);
      }
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setPaying(false);
    }
  }

  async function setChannel(c) {
    try {
      await api.patch(`/merchant/preferred-channel?channel=${c}`);
      toast.success(`Notifications switched to ${c.toUpperCase()}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  async function downloadInvoice(premium) {
    try {
      const { data: blob } = await api.get(
        `/pdfs/premiums/${premium.id}/invoice`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice_${premium.reference}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  async function downloadDecisionLetter(claim) {
    try {
      const { data: blob } = await api.get(
        `/pdfs/claims/${claim.id}/decision-letter`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `claim_decision_${claim.claim_number}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  async function runMerchantOcr(claim) {
    try {
      const { data: r } = await api.post(`/claims/${claim.id}/ocr-prescreen`);
      if (r.ok) {
        toast.success(`Documents scanned for ${claim.claim_number}`);
      } else {
        toast.message("Nothing to scan", {
          description: r.error === "no_evidence_files"
            ? "Attach evidence before scanning."
            : (r.error || "Scan not available right now."),
        });
      }
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  async function downloadBundle() {
    try {
      const { data: blob } = await api.get(
        `/pdfs/merchants/${merchant.id}/bundle.zip`, { responseType: "blob" },
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fortiqa_bundle_${(merchant.business_name || "merchant").replace(/\s+/g, "_")}.zip`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  async function downloadImprovementPlan() {
    try {
      toast.message("Coach is drafting your plan…");
      const { data: blob } = await api.get(
        "/pdfs/coach/improvement-plan.pdf", { responseType: "blob" },
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fortiqa_improvement_plan_${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  if (!data) return <div className="p-8 text-[#5c6360]" data-testid="loading-cover">{t("merchant_dashboard.loading_cover")}</div>;
  const { merchant, policies, claims, loyalty, risk_snapshots = [], premiums = [] } = data;
  const activePolicy = policies.find((p) => p.status === "active" || p.status === "cooling_off") || policies[0];
  const preferredChannel = merchant.preferred_channel || "whatsapp";
  const baseAmount = activePolicy?.premium_amount || 0;
  const discountedAmount = loyalty?.eligible ? Math.round(baseAmount * 0.9) : baseAmount;
  const snaps = risk_snapshots.map((s) => ({
    week: new Date(s.captured_at).toLocaleDateString("en", { month: "short", day: "2-digit" }),
    score: s.cyber_score,
  }));
  const currentScore = merchant.risk_score || 0;

  return (
    <div className="p-8 space-y-6 max-w-7xl" data-testid="merchant-dashboard">
      <Toaster position="top-right" />
      {/* Header */}
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#c15337] flex items-center gap-1.5">
            <Zap className="w-3 h-3" /> {t("merchant_dashboard.karibu")}
          </div>
          <h1 className="font-heading text-4xl font-bold mt-1 text-[#114232]">{merchant.business_name}</h1>
          <p className="text-sm text-[#5c6360] mt-1">{merchant.owner_name} · <span className="font-mono">{merchant.phone}</span></p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <button
            type="button"
            onClick={downloadBundle}
            data-testid="merchant-bundle-btn"
            title="Download all your PDFs (invoices, decision letters, KFS) as a single ZIP"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-[11px] uppercase tracking-wider font-semibold border border-[#114232]/15 rounded-md text-[#114232] hover:bg-[#114232]/5"
          >
            <Download className="w-3.5 h-3.5" /> Bundle ZIP
          </button>
          <button
            type="button"
            onClick={downloadImprovementPlan}
            data-testid="merchant-plan-btn"
            title="Generate a personalised CyberScore improvement plan PDF"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-[11px] uppercase tracking-wider font-semibold border border-[#114232]/15 rounded-md text-[#114232] hover:bg-[#114232]/5"
          >
            <Sparkles className="w-3.5 h-3.5" /> Improvement plan
          </button>
          <div
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-[0.16em] border ${
              live.connected
                ? "bg-[#114232]/6 text-[#114232] border-[#114232]/20"
                : "bg-[#5c6360]/8 text-[#5c6360] border-[#5c6360]/20"
            }`}
            data-testid="merchant-sse-status"
          >
            <Radio className={`w-3 h-3 ${live.connected ? "animate-pulse text-[#c15337]" : ""}`} />
            {live.connected ? `Live · ${live.beats}` : "Offline"}
          </div>
          <Button
            onClick={payPremium}
            disabled={paying || !activePolicy}
            data-testid="cta-pay-premium"
            className="btn-terracotta rounded-full px-5 h-11 flash-pulse hover:bg-[var(--terracotta-hover)]"
          >
            {paying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
            {t("merchant_dashboard.pay_premium", { amount: discountedAmount || "—" })}
            {loyalty?.eligible && (
              <span className="ml-2 text-[10px] bg-[#d49a36] text-[#1b1d1c] px-1.5 py-0.5 rounded-sm font-bold">
                −10%
              </span>
            )}
          </Button>
          <Link
            to="/merchant/claim"
            data-testid="cta-file-claim"
            className="inline-flex items-center gap-2 bg-white border border-[#114232]/15 hover:border-[#114232]/40 text-[#114232] px-4 h-11 rounded-full text-sm font-medium transition-colors"
          >
            <FilePlus className="w-4 h-4" /> {t("merchant_nav.file_claim")}
          </Link>
        </div>
      </header>

      {/* Channel toggle */}
      <div
        className="bg-white border border-[#114232]/10 rounded-lg p-4 flex items-center justify-between"
        data-testid="channel-toggle"
      >
        <div className="flex items-center gap-2 text-sm">
          <MessageCircle className="w-4 h-4 text-[#5c6360]" />
          <span className="font-medium">{t("merchant_dashboard.notifications_via")}</span>
          <span className="text-[#5c6360]">— {t("merchant_dashboard.currently")} {preferredChannel.toUpperCase()}</span>
        </div>
        <div className="inline-flex border border-[#114232]/15 rounded-full overflow-hidden text-xs">
          {["whatsapp", "sms"].map((c) => (
            <button
              key={c}
              onClick={() => setChannel(c)}
              data-testid={`channel-${c}`}
              className={`px-3.5 py-1.5 uppercase tracking-wider font-semibold transition-colors ${
                preferredChannel === c
                  ? "bg-[#114232] text-[#f4f2eb]"
                  : "bg-white hover:bg-[#114232]/5 text-[#5c6360]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {/* Above-the-fold free-week mirror banner */}
      {(merchant?.free_weeks_credit || 0) > 0 && (
        <div
          className="flex items-center justify-between gap-4 p-4 rounded-md bg-gradient-to-r from-[#d49a36]/20 via-[#d49a36]/8 to-transparent border border-[#d49a36]/40 flex-wrap"
          data-testid="merchant-free-week-banner"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#d49a36]/25 flex items-center justify-center">
              <Zap className="w-5 h-5 text-[#8a5a16]" strokeWidth={1.8} />
            </div>
            <div>
              <div className="font-heading text-base font-bold text-[#114232]">
                You have <span className="text-[#8a5a16]">{merchant.free_weeks_credit}</span>{" "}
                free week{merchant.free_weeks_credit === 1 ? "" : "s"} of cover
              </div>
              <div className="text-xs text-[#5c6360]">
                Redeem now — your next premium settles at KES 0.
              </div>
            </div>
          </div>
          <Button
            onClick={payPremium}
            disabled={paying || !activePolicy}
            data-testid="merchant-free-week-redeem"
            className="btn-baobab rounded-full px-5 h-10 text-xs uppercase tracking-wider font-semibold hover:bg-[var(--baobab-hover)]"
          >
            {paying ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Zap className="w-3.5 h-3.5 mr-1.5" />}
            Redeem free week
          </Button>
        </div>
      )}

      {/* Cover + Loyalty + Score */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Active policy */}
        <div className="metric-card lg:col-span-2" data-testid="active-policy-card">
          <div className="text-[11px] uppercase tracking-[0.18em] text-[#5c6360] font-semibold flex items-center gap-2">
            <FileText className="w-3.5 h-3.5" /> {t("merchant_dashboard.your_cover")}
          </div>
          {activePolicy ? (
            <>
              <div className="mt-3 flex items-baseline justify-between flex-wrap gap-3">
                <div>
                  <div className="font-heading text-2xl font-bold text-[#114232]">
                    {TIER_LABEL[activePolicy.tier] || activePolicy.tier}
                  </div>
                  <div className="text-xs text-[#5c6360] font-mono mt-1">{activePolicy.policy_number}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-3xl font-bold text-[#114232]">
                    KES {activePolicy.cover_limit.toLocaleString()}
                  </div>
                  <div className="text-[11px] text-[#5c6360] uppercase tracking-wider">
                    {t("merchant_dashboard.cover_limit")}
                  </div>
                </div>
              </div>
              <div className="mt-5 grid grid-cols-3 gap-4 text-sm">
                <div>
                  <div className="text-[10px] text-[#5c6360] uppercase tracking-wider font-semibold">
                    {t("merchant_dashboard.weekly")}
                  </div>
                  <div className="font-mono mt-1 text-base">KES {activePolicy.premium_amount}</div>
                </div>
                <div>
                  <div className="text-[10px] text-[#5c6360] uppercase tracking-wider font-semibold">
                    {t("merchant_dashboard.status")}
                  </div>
                  <div className="mt-1">
                    <span className="badge-status badge-active">{activePolicy.status?.replace(/_/g, " ")}</span>
                  </div>
                </div>
                <div>
                  <div className="text-[10px] text-[#5c6360] uppercase tracking-wider font-semibold">
                    {t("merchant_dashboard.renewal")}
                  </div>
                  <div className="font-mono mt-1 text-xs">
                    {activePolicy.renewal_date ? new Date(activePolicy.renewal_date).toLocaleDateString() : "—"}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="text-sm text-[#5c6360] mt-3">{t("merchant_dashboard.no_active_policy")}</div>
          )}
        </div>

        {/* Loyalty + Score */}
        <div className="metric-card flex flex-col" data-flash={loyalty?.eligible ? "true" : "false"} data-testid="loyalty-status-card">
          <div className="flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#5c6360] font-semibold flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5 text-[#d49a36]" /> {t("merchant_dashboard.loyalty")}
            </div>
            <div
              className="score-ring"
              style={{ "--p": currentScore, "--c": currentScore >= 80 ? "#114232" : currentScore >= 50 ? "#d49a36" : "#c15337" }}
              data-testid="score-ring"
            >
              <span>{currentScore}</span>
            </div>
          </div>
          <div className="mt-3 flex-1">
            {loyalty?.eligible ? (
              <>
                <span className="badge-status badge-success inline-flex items-center gap-1">
                  <ShieldCheck className="w-3 h-3" /> {t("merchant_dashboard.rebate_active")}
                </span>
                <div className="text-xs text-[#5c6360] mt-2">
                  {t("merchant_dashboard.next_premium")}: <span className="font-mono text-[#114232]">KES {Math.round((activePolicy?.premium_amount || 0) * 0.9)}</span>
                </div>
              </>
            ) : loyalty?.paused ? (
              <>
                <span className="badge-status badge-suspended inline-flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> {t("merchant_dashboard.paused")}
                </span>
                <div className="text-xs text-[#5c6360] mt-2">{loyalty.pause_reason}</div>
              </>
            ) : (
              <>
                <span className="badge-status badge-pending">{t("merchant_dashboard.building_streak")}</span>
                <div className="text-xs text-[#5c6360] mt-2">
                  {t("merchant_dashboard.weeks_above_80", { count: (loyalty?.last_scores || []).filter(s => s >= 80).length })}
                </div>
              </>
            )}
          </div>
          <div className="mt-3 flex gap-1">
            {(loyalty?.last_scores || []).map((s, i) => (
              <span
                key={i}
                className={`font-mono text-[11px] px-2 py-0.5 rounded-sm ${
                  s >= 80
                    ? "bg-[#114232]/8 text-[#114232] ring-1 ring-[#114232]/20"
                    : "bg-[#d49a36]/12 text-[#8a5a16] ring-1 ring-[#d49a36]/30"
                }`}
              >
                {s}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* KYC */}
      <KycCard merchant={merchant} onSubmitted={load} />

      {/* Refer-a-friend */}
      <ReferralCard onPayWithCredit={load} />

      {/* KYC review history timeline */}
      <MerchantDocTimeline />

      {/* CyberScore chart */}
      {snaps.length > 0 && (
        <div className="bg-white border border-[#114232]/10 rounded-lg p-5" data-testid="merchant-chart">
          <div className="flex items-center justify-between mb-4">
            <div className="font-heading text-base font-semibold text-[#114232]">
              {t("merchant_dashboard.your_cyberscore")}
            </div>
            <div className="text-xs text-[#5c6360]">{t("merchant_dashboard.threshold_80")}</div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={snaps} margin={{ top: 10, right: 16, left: -16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(17,66,50,0.08)" vertical={false} />
              <XAxis dataKey="week" stroke="#5c6360" fontSize={11} />
              <YAxis domain={[0, 100]} stroke="#5c6360" fontSize={11} />
              <Tooltip
                contentStyle={{
                  background: "#fff",
                  border: "1px solid rgba(17,66,50,0.15)",
                  borderRadius: 6,
                  fontSize: 12,
                }}
              />
              <ReferenceLine y={80} stroke="#d49a36" strokeDasharray="4 4" />
              <Line
                type="monotone"
                dataKey="score"
                stroke="#114232"
                strokeWidth={2.5}
                dot={{ r: 4, fill: "#114232" }}
                activeDot={{ r: 6, fill: "#c15337" }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Claims table */}
      <div className="bg-white border border-[#114232]/10 rounded-lg overflow-hidden" data-testid="my-claims-card">
        <div className="px-5 py-4 border-b border-[#114232]/10 font-heading text-base font-semibold text-[#114232]">
          {t("merchant_dashboard.your_claims")}
        </div>
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-[0.1em] text-[#5c6360] bg-[#114232]/3">
            <tr>
              <th className="text-left font-semibold py-3 px-5">{t("merchant_dashboard.claim_number")}</th>
              <th className="text-left font-semibold py-3 px-5">{t("merchant_dashboard.type")}</th>
              <th className="text-left font-semibold py-3 px-5">{t("merchant_dashboard.amount")}</th>
              <th className="text-left font-semibold py-3 px-5">{t("merchant_dashboard.status")}</th>
              <th className="text-right font-semibold py-3 px-5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {claims.map((c) => (
              <React.Fragment key={c.id}>
              <tr className="border-t border-[#114232]/10" data-testid={`my-claim-${c.id}`}>
                <td className="py-3 px-5 font-mono text-xs">{c.claim_number}</td>
                <td className="py-3 px-5 capitalize">{c.claim_type.replace(/_/g, " ")}</td>
                <td className="py-3 px-5 font-mono">KES {c.amount_claimed.toLocaleString()}</td>
                <td className="py-3 px-5">
                  <span className={`badge-status ${CLAIM_BADGE[c.status] || "badge-pending"}`}>
                    {c.status.replace(/_/g, " ")}
                  </span>
                </td>
                <td className="py-3 px-5 text-right">
                  <div className="inline-flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => runMerchantOcr(c)}
                      disabled={!c.evidence_urls?.length}
                      data-testid={`my-claim-ocr-${c.id}`}
                      title={c.evidence_urls?.length ? "Rescan attached documents" : "No evidence files attached"}
                      className="inline-flex items-center gap-1 px-2 py-1 text-[11px] uppercase tracking-wider border border-[#114232]/15 rounded-md text-[#114232] hover:bg-[#114232]/5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <Scan className="w-3 h-3" /> Scan
                    </button>
                    <button
                      type="button"
                      onClick={() => downloadDecisionLetter(c)}
                      disabled={["submitted", "validating", "manual_review"].includes(c.status)}
                      data-testid={`my-claim-letter-${c.id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 text-[11px] uppercase tracking-wider border border-[#114232]/15 rounded-md text-[#114232] hover:bg-[#114232]/5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <Download className="w-3 h-3" /> PDF
                    </button>
                  </div>
                </td>
              </tr>
              <tr className="border-t border-[#114232]/5" data-testid={`my-claim-timeline-${c.id}`}>
                <td colSpan={5} className="px-5 py-3 bg-[#114232]/2">
                  <ClaimTimeline claimId={c.id} />
                </td>
              </tr>
              </React.Fragment>
            ))}
            {claims.length === 0 && (
              <tr>
                <td colSpan={5} className="py-10 text-center text-[#5c6360]">
                  {t("merchant_dashboard.no_claims")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Premium invoices */}
      <div className="bg-white border border-[#114232]/10 rounded-lg overflow-hidden" data-testid="merchant-invoices-card">
        <div className="px-5 py-4 border-b border-[#114232]/10">
          <div className="font-heading text-base font-semibold text-[#114232]">
            {t("merchant_invoices.title")}
          </div>
          <div className="text-xs text-[#5c6360] mt-0.5">{t("merchant_invoices.subtitle")}</div>
        </div>
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-[0.1em] text-[#5c6360] bg-[#114232]/3">
            <tr>
              <th className="text-left font-semibold py-3 px-5">{t("merchant_invoices.reference")}</th>
              <th className="text-left font-semibold py-3 px-5">{t("merchant_invoices.date")}</th>
              <th className="text-left font-semibold py-3 px-5">{t("merchant_invoices.amount")}</th>
              <th className="text-left font-semibold py-3 px-5">{t("merchant_invoices.status")}</th>
              <th className="text-right font-semibold py-3 px-5">{t("merchant_invoices.invoice")}</th>
            </tr>
          </thead>
          <tbody>
            {premiums.slice(0, 10).map((p) => (
              <tr key={p.id} className="border-t border-[#114232]/10" data-testid={`merchant-premium-${p.id}`}>
                <td className="py-3 px-5 font-mono text-xs">{p.reference}</td>
                <td className="py-3 px-5 text-xs text-[#5c6360] font-mono">
                  {new Date(p.created_at).toLocaleDateString()}
                </td>
                <td className="py-3 px-5 font-mono">KES {p.amount.toLocaleString()}</td>
                <td className="py-3 px-5">
                  <span className={`badge-status ${
                    p.status === "success" ? "badge-paid" :
                    p.status === "failed" ? "badge-rejected" : "badge-pending"
                  }`}>
                    {p.status}
                  </span>
                </td>
                <td className="py-3 px-5 text-right">
                  <button
                    type="button"
                    onClick={() => downloadInvoice(p)}
                    disabled={p.status !== "success"}
                    data-testid={`merchant-invoice-download-${p.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider border border-[#114232]/15 rounded-md bg-white hover:bg-[#114232]/5 text-[#114232] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Download className="w-3.5 h-3.5" /> {t("merchant_invoices.download")}
                  </button>
                </td>
              </tr>
            ))}
            {premiums.length === 0 && (
              <tr>
                <td colSpan={5} className="py-10 text-center text-[#5c6360]">
                  {t("merchant_invoices.empty")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <CoachWidget />
    </div>
  );
}
